import { removeLocalStorage } from '../util/Util.js';
import { router } from '../main.js';
import axios from 'axios';
import i18n from '../i18n.js';
import SweetAlert from 'src/class/Alerts';

export default {
  // le decimos a vuex que haremos uso de los espacios de nombre, para llarmalo por auth
  namespaced: true,

  // aca definimos nuestros estados, especie de variables
  state: {
    token: null,
    user: null,
    permissions: [],
  },

  // definimos los metodos que retornaran los valores de nuestros estados
  getters: {
    authenticated(state) {
      return state.token && state.user;
    },

    user(state) {
      return state.user;
    },

    permissions(state) {
      return state.permissions;
    },
  },

  // definimos los metodos para modificar (mutar) nuestros estados
  mutations: {
    //
    SET_TOKEN(state, token) {
      state.token = token;
    },

    SET_USER(state, data) {
      state.user = data;
    },

    SET_PERMISSION(state, data) {
      state.permissions = data;
    },
  },

  // definimos las acciones, funciones que hacen uso de nuestros estados
  actions: {
    /**
     * hace uso de axios para realizar una peticion de login a
     * la api enviando las credenciales de usuario
     * @param  {JSON}
     * @return  {Promise}
     */
    async login({ dispatch }, credentials) {
      // hacemos la peticion por axios
      let response = await axios.post('/auth/login', credentials);

      // si la peticion retorno un token de acceso llamamos a otra acción
      if (response.data.data.access_token) {
        return dispatch('attempt', response.data.data.access_token);
      }

      // si la peticion no retorno un token, devolvemos su respuesta para tratarla
      return response;
    },

    /** esta accion, una vez obtenido el usuario a traves
     * de google auth, autenticarlo de forma manual en
     * nuestra aplicacion
     * @param  {JSON}
     * @return  {Promise}
     */
    async loginGoogle({ dispatch }, access_token) {
      return dispatch('attempt', access_token);
    },

    /**
     * esta accion recibe el token de acceso de la api
     * y a traves de las mutaciones lo guarda en nuestros
     * estados, hace lo mismo con los datos de usuario.
     * @param  {JSON}
     * @return  {Promise}
     */
    async attempt({ dispatch, commit, state }, token) {
      // si existe el token, se guarda en el estado
      if (token) {
        commit('SET_TOKEN', token);
      }

      // si no se pudo guardar el token, detiene la autenticacion
      if (!state.token) {
        return;
      }

      // tratamos de hacer una peticion a la api para obtener los datos del usuario
      try {
        // si los datos se obtienen los guardamos en el estado user
        let response = await axios.get('/auth/user');
        commit('SET_USER', response.data);
        localStorage.setItem('downtime', response.data.downtime);
        await dispatch('syncPermissions', response.data.permissions);

        response.data.roles.forEach((element) => {
          dispatch('asignPermissions', element.permissions);
        });

        return true;
      } catch (error) {
        // en caso de error reseteamos nuestros estados a null
        console.log(error);
        commit('SET_TOKEN', null);
        commit('SET_USER', null);
        commit('SET_PERMISSION', []);

        return false;
      }
    },

    /**
     * esta accion se encagar de limpiar el token,
     * para deslogear al usuario
     */
    async logout({ commit }) {
      await axios.get('/auth/logout').then(() => {
        sessionStorage.clear();
        removeLocalStorage('locale');
        commit('SET_TOKEN', null);
      });
    },

    /**
     * esta accion se encagar de limpiar la sesión
     * del usuario
     */
    async closeAllUserSessions(_, data) {
      return await axios.post('/auth/closeallusersessions', data);
    },

    /**
     * Este metodo se encarga de revokar el token de acceso
     * a la aplicación
     */
    async revokeAccessToken(_) {
      return await axios.get('/auth/revokeaccesstoken');
    },

    /*
     * esta accion se encagar de limpiar el usuario, una vez
     * se llevo al usuario al login despues de deslogearse
     */
    cleanUserlogout({ commit }) {
      commit('SET_USER', null);
      commit('SET_PERMISSION', []);
    },

    /**
     * esta accion se encarga de hacer la peticion a la api
     * para reestablecer la contraseña del usuario, enviandole
     * los datos del mismo, y de haber realizado el proceso
     * correctamente, llamar a la accion para autenticarlo
     * @param  {JSON}
     * @return  {Promise}
     */
    async passwordReset({ dispatch }, credentials) {
      await axios.post('/auth/reset', credentials).then(() => {
        let data = {
          login: credentials.login,
          password: credentials.password,
          remember_me: false,
        };
        return dispatch('login', data);
      });
    },

    /**
     * esta accion se encarga de hacer la peticion a la api
     * para crear un token de reset para un usuario, enviandole
     * el correo del mismo
     * @param  {JSON}
     * @return  {Promise}
     */
    async passwordRequest(_, credentials) {
      return await axios.post('/auth/create', credentials);
    },

    /**
     * Esta acción se encarga de validar el estado de la sesión del
     * usuario logeado, si la sesión es activa lo deja seguir
     * conectado a la aplicación, por el contrario le cerrará la
     * sesión.
     */
    async validateActiveSession({ dispatch }) {
      return await axios.post('/auth/users/getsession');
    },

    /**
     * esta accion se encarga de hacer la peticion a la api
     * para verificar si la contraseña ha expirado, de ser
     * asi muestra una alerta y llama a la accion para desloguear,
     * lleva al usuario al login y limpia todos los datos del mismo
     * @param  {JSON}
     * @return  {Promise}
     */
    async validatePassTime({ dispatch }, credentials) {
      await axios
        .post('/auth/validatepass', { id: credentials })
        .then((response) => {
          if (response.data.code == 1) {
            SweetAlert.warning(i18n.t('sto.auth.password_expired_text'));
            dispatch('logout');
            setTimeout(() => {
              dispatch('cleanUserlogout');
              sessionStorage.clear();
              removeLocalStorage('locale');
              router.push('/login');
            }, 5000);
          }
          return;
        })
        .catch((error) => {
          console.log(error);
        });
    },

    async asignPermissions({ commit, state }, data) {
      let array = [];
      if (Array.isArray(data)) {
        state.permissions.forEach((element) => {
          array.push(element);
        });

        for (let index = 0; index < data.length; index++) {
          const element = data[index];
          if (!array.includes(element.name)) array.push(element.name);
        }
      } else {
        array = state.permissions;
        if (!array.includes(data)) array.push(data);
      }

      commit('SET_PERMISSION', array);
      return;
    },

    async syncPermissions({ dispatch, commit }, data) {
      commit('SET_PERMISSION', []);
      if (data.length > 0) dispatch('asignPermissions', data);
      return;
    },
  },
};
